import React, { useState, useEffect, useCallback } from 'react';
import { FaceLivenessDetector } from '@aws-amplify/ui-react-liveness';
import es from './locales/es';

export default function FaceLiveness({resultUrl}) {
    const [isRetrying, setIsRetrying] = useState(false);
    const [sessionId, setSessionId] = useState(window.sessionId);
    const [isStarted, setIsStarted] = useState(false);
    const [resultStatus, setResultStatus] = useState(null);

    const stopCamera = () => {
        setResultStatus('error');

        if (window.stream) {
            window.stream.getTracks().forEach((track) => track.stop());
        }

        document.querySelectorAll('video').forEach((videoEl) => {
            if (videoEl.srcObject) {
                const stream = videoEl.srcObject;
                if (stream && stream.getTracks) {
                    stream.getTracks().forEach((track) => track.stop());
                }
                videoEl.srcObject = null;
            }
        });
    };

    const handleError = useCallback((errorDetail) => {
        stopCamera();
        setIsStarted(false);
        window.dispatchEvent(
            new CustomEvent('rekognition:error', {
                detail: { error: errorDetail },
            })
        );
    }, []);

    const handleCancel = useCallback(() => {
        stopCamera();
        setIsStarted(false);
        window.dispatchEvent(
            new CustomEvent('rekognition:canceled', {
                detail: { message: 'El usuario canceló el proceso.' },
            })
        );
    }, []);

    const handleRetry = () => {
        if (isRetrying) return;

        stopCamera();

        setIsRetrying(true);
        setResultStatus(null);

        window.dispatchEvent(
            new CustomEvent('rekognition:retry')
        );
    };
    useEffect(() => {
        if (!isStarted) return;

        const handleKeyDown = (e) => {
            if (e.key === 'Escape' || e.key === 'Enter') {
                handleCancel();
            }
        };

        const handleClick = (e) => {
            const target = e.target;

            // Detecta clics en cualquier botón de cierre (X)
            const isCloseBtn = target.closest('button[aria-label*="close"]') ||
                target.closest('button[aria-label*="Cerrar"]') ||
                target.closest('.amplify-button--close') ||
                target.innerText === '×' || target.innerText === 'X';

            /*const isRetryBtn = target.innerText?.includes('Intentar nuevamente') ||
                target.closest('button')?.innerText?.includes('Intentar nuevamente');*/

            if (isCloseBtn) {
                handleCancel();
            } /*else if (isRetryBtn) {
                stopCamera();
                setIsStarted(false);
                setTimeout(() => setIsStarted(true), 100);
            }*/
        };

        window.addEventListener('keydown', handleKeyDown);
        document.addEventListener('click', handleClick);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('click', handleClick);
        };
    }, [isStarted, handleCancel]);

    useEffect(() => {
        return () => {
            stopCamera();
        };
    }, []);

    useEffect(() => {
        const handleStart = (event) => {
            const newSessionId = event.detail?.sessionId;

            if (!newSessionId) return;

            setSessionId(newSessionId);
            setIsRetrying(false);
            setResultStatus(null);
            setIsStarted(true);
        };

        window.addEventListener('rekognition:start', handleStart);

        return () => {
            window.removeEventListener('rekognition:start', handleStart);
        };
    }, []);

    // 1. PANTALLA DE ESPERA

    if (isRetrying) {
        return (
            <div className="liveness-retry-loading">
                <div className="liveness-retry-spinner"></div>

                <h2>Preparando nueva verificación</h2>

                <p>
                    Estamos iniciando una nueva sesión de verificación.
                </p>
            </div>
        );
    }

    // 2. PANTALLA DE ERROR
    if (resultStatus === 'error') {
        return (
            <div className="liveness-welcome-container">
                <div className="liveness-welcome-icon liveness-error-icon">
                    <svg viewBox="0 0 24 24"> <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" /> </svg>
                </div>
                <h2>No fue posible verificar tu identidad</h2>
                <p className="liveness-welcome-subtitle"> Ocurrió un problema durante la verificación facial. </p>
                <p className="liveness-welcome-instruction"> Por favor, inténtalo nuevamente. Asegúrate de estar en un lugar bien iluminado y de mantener tu rostro visible. </p>
                <button className="liveness-start-button" onClick={handleRetry} > Intentar nuevamente </button>
            </div>);
    }

    //3. PANTALA DE SUCCESS

    if (resultStatus === 'success') {
        return (
            <div className="liveness-welcome-container">
                <div className="liveness-welcome-icon liveness-success-icon">
                    <svg viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                    </svg>
                </div>

                <h2>Identidad verificada correctamente</h2>

                <p className="liveness-welcome-subtitle">
                    Tu identidad fue verificada exitosamente.
                </p>

                <p className="liveness-welcome-instruction">
                    La verificación facial se completó correctamente. Puedes continuar con el proceso.
                </p>
            </div>
        );
    }

    // 4. PANTALLA PREVIA DE INSTRUCCIONES
    if (!isStarted) {
        return (
            <div className="liveness-welcome-container">
                <div className="liveness-welcome-icon">
                    <svg viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 4c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6zm0 14c-2.03 0-3.8-1.04-4.83-2.6.03-1.6 3.22-2.4 4.83-2.4s4.8 0.8 4.83 2.4c-1.03 1.56-2.8 2.6-4.83 2.6z" />
                    </svg>
                </div>

                <h2>Verificación facial</h2>
                <p className="liveness-welcome-subtitle">
                    Confirma tu identidad mediante una breve verificación facial.
                </p>

                <p className="liveness-welcome-instruction">
                    Asegúrate de estar en un lugar bien iluminado, mantén tu rostro
                    visible y sigue las instrucciones que aparecerán en pantalla.
                </p>

                <button className="liveness-start-button"
                    onClick={() => {
                        setResultStatus(null);
                        setIsStarted(true);
                    }} >
                    Comenzar verificación </button>
            </div>
        );
    }


    // 5. DETECTOR DE AWS REKOGNITION
    return (
        <FaceLivenessDetector
            sessionId={window.sessionId}
            region="us-east-1"
            displayText={es}
            disableStartScreen={true}
            onAnalysisComplete={async () => {
                const csrf = document
                    .querySelector('meta[name="csrf-token"]')
                    ?.getAttribute('content');

                try {
                    const response = await fetch(resultUrl, {
                    //const response = await fetch('/rekognition/liveness/result', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Accept': 'application/json',
                            'X-CSRF-TOKEN': csrf,
                        },
                        body: JSON.stringify({
                            sessionId: window.sessionId,
                        }),
                    });

                    const result = await response.json();

                    stopCamera();


                    if (!result.success) {
                        setIsStarted(false);
                        setResultStatus('error');
                    }else{
                        setResultStatus('success');
                    }

                    window.dispatchEvent(
                        new CustomEvent('rekognition:completed', {
                            detail: {
                                success: result.success,
                                result,
                            },
                        })
                    );
                } catch (error) {
                    handleError(error);
                    setResultStatus('error');
                }

                setIsStarted(false);
            }}
            onError={(error) => {
                handleError(error);
            }}
            onUserCancel={() => {
                handleCancel();
                setResultStatus('error');
            }}
        />
    );
}
